import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, TextField, Table, TableBody, TableCell, TableHead, TableRow,
  Button, Modal, IconButton, Pagination, Alert, Grid, CircularProgress
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { db } from './firebase'; // Your Firebase config file
import { collection, doc, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import Layout from './Layout';
import AppContext from './AppContext';

const StockManagement = () => {
  const { shopId, t } = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [stockForm, setStockForm] = useState({
    stockCode: '',
    description: '',
    quantity: 1,
    dateAdded: new Date().toISOString().split('T')[0]
  });
  const [editStockId, setEditStockId] = useState(null);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch products from Firebase
  useEffect(() => {
    if (!shopId) {
      setProducts([]);
      setSelectedProduct(null);
      setLoadError(t('Shop access not configured.'));
      setLoading(false);
      return;
    }

    setLoadError('');
    setLoading(true);

    const productsQuery = query(collection(db, 'products'), where('shopId', '==', shopId));

    // Listen for real-time changes so UI stays in sync with Firestore
    const unsubscribe = onSnapshot(
      productsQuery,
      (snapshot) => {
        const productsData = snapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            if (!data || typeof data !== 'object') {
              return null;
            }
            return { id: docSnap.id, ...data };
          })
          .filter((product) => product && product.productType === 'Sale');

        const sortedProducts = productsData.sort((a, b) => a.name.localeCompare(b.name));
        setProducts(sortedProducts);
        setLoading(false);
      },
      (listenerError) => {
        console.error('Failed to subscribe to products', listenerError);
        setLoadError('Failed to load products. Please try again.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [shopId]);

  useEffect(() => {
    if (!selectedProduct) {
      return;
    }

    const freshSelection = products.find((product) => product.id === selectedProduct.id);
    if (freshSelection) {
      setSelectedProduct(freshSelection);
      return;
    }

    setSelectedProduct(null);
    setOpenModal(false);
  }, [products, selectedProduct?.id]);

  // Filter products based on search
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const paginatedProducts = filteredProducts.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [filteredProducts.length, itemsPerPage, page]);

  // Generate unique stock code
  const generateStockCode = () => {
    const code = `#${Math.floor(100000 + Math.random() * 900000)}`;
    setStockForm((prev) => ({ ...prev, stockCode: code }));
  };

  // Open modal and set selected product
  const handleManageStock = (product) => {
    setError('');
    setSelectedProduct(product);
    setOpenModal(true);
    setStockForm({
      stockCode: '',
      description: '',
      quantity: 1,
      dateAdded: new Date().toISOString().split('T')[0]
    });
    setEditStockId(null);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStockForm({ ...stockForm, [name]: value });
  };

  // Save or update stock
  const saveStock = async () => {
    setError('');

    if (!selectedProduct) {
      setError(t('Select a product before updating stock.'));
      return;
    }

    const trimmedCode = stockForm.stockCode.trim();
    if (!trimmedCode) {
      setError(t('Stock Code is required.'));
      return;
    }

    const parsedQuantity = Number(stockForm.quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setError(t('Quantity must be greater than 0'));
      return;
    }

    const normalizedStock = {
      ...stockForm,
      stockCode: trimmedCode,
      quantity: parsedQuantity,
    };

    const stockExists = selectedProduct?.stock?.some(
      (entry) => entry.stockCode === normalizedStock.stockCode && entry.stockCode !== editStockId
    );
    if (stockExists) {
      setError(t('Stock Code must be unique'));
      return;
    }

    const updatedStock = editStockId
      ? (selectedProduct.stock || []).map((entry) =>
          entry.stockCode === editStockId ? normalizedStock : entry
        )
      : [...(selectedProduct.stock || []), normalizedStock];

    try {
      const productRef = doc(db, 'products', selectedProduct.id);
      await updateDoc(productRef, { stock: updatedStock });
      setSelectedProduct((current) => {
        if (!current || current.id !== selectedProduct.id) {
          return current;
        }
        return { ...current, stock: updatedStock };
      });
      setStockForm({
        stockCode: '',
        description: '',
        quantity: 1,
        dateAdded: new Date().toISOString().split('T')[0]
      });
      setEditStockId(null);
      setError('');
    } catch (err) {
      setError(t('Failed to save stock'));
    }
  };

  // Edit stock entry
  const handleEditStock = (stockEntry) => {
    setError('');
    setStockForm({
      ...stockEntry,
      quantity: String(stockEntry.quantity ?? '1'),
    });
    setEditStockId(stockEntry.stockCode);
  };

  // Delete stock entry
  const handleDeleteStock = async (stockCode) => {
   // if (!window.confirm('Are you sure you want to delete this stock entry?')) return;

    if (!selectedProduct) {
      setError(t('Select a product before deleting stock.'));
      return;
    }

    setError('');

    const updatedStock = (selectedProduct.stock || []).filter(
      entry => entry.stockCode !== stockCode
    );
    try {
      const productRef = doc(db, 'products', selectedProduct.id);
      await updateDoc(productRef, { stock: updatedStock });
      setSelectedProduct((current) => {
        if (!current || current.id !== selectedProduct.id) {
          return current;
        }
        return { ...current, stock: updatedStock };
      });
    } catch (err) {
      setError(t('Failed to delete stock'));
    }
  };

  const totalStock = selectedProduct?.stock?.reduce(
    (sum, entry) => sum + Number(entry.quantity),
    0
  ) || 0;

  return (
    <Layout>
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t("Stock Management")}</Typography>
        <TextField
          label={t("Search Products")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

      {/* Product List */}
      <Table>
        <TableHead>
          <TableRow>
                  <TableCell>{t("Product Name")}</TableCell>
                  <TableCell>{t("Current Stock")}</TableCell>
                  <TableCell>{t("Actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          )}
          {!loading && paginatedProducts.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} align="center">
                No products found.
              </TableCell>
            </TableRow>
          )}
          {!loading && paginatedProducts.map(product => (
            <TableRow key={product.id}>
              <TableCell>{product.name}</TableCell>
              <TableCell>
                {product.stock?.reduce((sum, entry) => sum + Number(entry.quantity), 0) || 0}
              </TableCell>
              <TableCell>
                <Button onClick={() => handleManageStock(product)}>
                  {t("Manage Stock")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage))}
          page={page}
          onChange={(e, value) => setPage(value)}
        />
      </Box>

      {/* Stock Management Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <Typography variant="h6">
            {t("Manage Stock for {name}", { name: selectedProduct?.name })}
          </Typography>

          {/* Current Stock Overview */}
          <Box sx={{ mt: 2 }}>
            <Typography>{t("Total Stock")}: {totalStock}</Typography>
            {selectedProduct?.stock?.length > 0 && (
              <Typography variant="caption">
                {t("Last Updated")}: {selectedProduct.stock.slice(-1)[0].dateAdded}
              </Typography>
            )}
          </Box>

          {/* Stock List */}
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("Stock Code")}</TableCell>
                <TableCell>{t("Description")}</TableCell>
                <TableCell>{t("Quantity")}</TableCell>
                <TableCell>{t("Date Added")}</TableCell>
                <TableCell>{t("Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedProduct?.stock?.length > 0 ? (
                selectedProduct.stock.map(entry => (
                  <TableRow key={entry.stockCode}>
                    <TableCell>{entry.stockCode}</TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>{entry.quantity}</TableCell>
                    <TableCell>{entry.dateAdded}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditStock(entry)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteStock(entry.stockCode)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>{t("No stock entries yet.")}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Add Stock Form */}
          <Box sx={{ mt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("Stock Code")}
                  name="stockCode"
                  value={stockForm.stockCode}
                  onChange={handleInputChange}
                  InputProps={{
                    endAdornment: (
                  <Button onClick={generateStockCode}>{t("Generate")}</Button>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("Quantity")}
                  name="quantity"
                  type="number"
                  value={stockForm.quantity}
                  onChange={handleInputChange}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("Description")}
                  name="description"
                  multiline
                  rows={2}
                  value={stockForm.description}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("Date Added")}
                  name="dateAdded"
                  type="date"
                  value={stockForm.dateAdded}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Modal Footer */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => setOpenModal(false)}>{t("Close")}</Button>
            <Button variant="contained" onClick={saveStock}>
              {editStockId ? 'Update Stock' : 'Save Stock'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
    </Layout>
  );
};

export default StockManagement;
